import React, { useMemo, useState } from 'react';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { useAppData } from '../../providers/AppDataProvider';
import { parseXeroDate } from '../../utils/xeroDate';
import MonthDetailModal from './MonthDetailModal';
import { NetCashFlowView } from './views/NetCashFlowView';
import { ReceivablesAgingView } from './views/ReceivablesAgingView';
import { TopCustomersView } from './views/TopCustomersView';
import { ExpenseBreakdownView } from './views/ExpenseBreakdownView';

type CashFlowViewMode = 'overview' | 'net-trend' | 'receivables' | 'top-customers' | 'expenses';
type PeriodKey = '1m' | '3m' | '6m' | '12m' | 'custom';

const VIEW_TABS: { key: CashFlowViewMode; label: string; icon: string }[] = [
  { key: 'overview', label: 'Overview', icon: 'bi-bar-chart-fill' },
  { key: 'net-trend', label: 'Net Trend', icon: 'bi-graph-up' },
  { key: 'receivables', label: 'Receivables', icon: 'bi-clock-history' },
  { key: 'top-customers', label: 'Top Customers', icon: 'bi-people-fill' },
  { key: 'expenses', label: 'Expenses', icon: 'bi-receipt' },
];

const PERIOD_OPTIONS: { key: PeriodKey; label: string }[] = [
  { key: '1m', label: '1 Month' },
  { key: '3m', label: '3 Months' },
  { key: '6m', label: '6 Months' },
  { key: '12m', label: '12 Months' },
  { key: 'custom', label: 'Custom' },
];

function getDateRange(period: PeriodKey, customFrom: string, customTo: string): { startDate: Date; endDate: Date } {
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  let start: Date;
  if (period === 'custom' && customFrom && customTo) {
    start = new Date(customFrom);
    start.setHours(0, 0, 0, 0);
    const toDate = new Date(customTo);
    toDate.setHours(23, 59, 59, 999);
    return { startDate: start, endDate: toDate };
  }
  const months = period === '1m' ? 1 : period === '3m' ? 3 : period === '6m' ? 6 : 12;
  start = new Date(end.getFullYear(), end.getMonth() - months + 1, 1);
  start.setHours(0, 0, 0, 0);
  return { startDate: start, endDate: end };
}

function getMonthsBetween(start: Date, end: Date): { year: number; month: number }[] {
  const out: { year: number; month: number }[] = [];
  const cur = new Date(start.getFullYear(), start.getMonth(), 1);
  const endFirst = new Date(end.getFullYear(), end.getMonth(), 1);
  while (cur <= endFirst) {
    out.push({ year: cur.getFullYear(), month: cur.getMonth() });
    cur.setMonth(cur.getMonth() + 1);
  }
  return out;
}

interface MonthlyData {
  month: string;
  monthLabel: string;
  monthFullLabel: string;
  cashIn: number;
  cashOut: number;
}

export function CashFlowSummary() {
  const { invoices, bills, xeroConnected } = useAppData();

  const [viewMode, setViewMode] = useState<CashFlowViewMode>('overview');
  const [selectedMonth, setSelectedMonth] = useState<MonthlyData | null>(null);
  const [period, setPeriod] = useState<PeriodKey>('6m');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [collapsed, setCollapsed] = useState(false);

  const { startDate, endDate } = useMemo(
    () => getDateRange(period, customFrom, customTo),
    [period, customFrom, customTo]
  );

  const periodLabel = useMemo(() => {
    if (period === 'custom' && customFrom && customTo) {
      const s = new Date(customFrom);
      const e = new Date(customTo);
      return `${s.toLocaleDateString('en-AU', { month: 'short', year: 'numeric' })} – ${e.toLocaleDateString('en-AU', { month: 'short', year: 'numeric' })}`;
    }
    if (period === '1m') return 'Last 1 month';
    if (period === '3m') return 'Last 3 months';
    if (period === '6m') return 'Last 6 months';
    if (period === '12m') return 'Last 12 months';
    return 'Select period';
  }, [period, customFrom, customTo]);

  // Calculate monthly cash flow data for the selected period (used by overview)
  const { monthlyData, totals } = useMemo(() => {
    const monthList = getMonthsBetween(startDate, endDate);
    const months: MonthlyData[] = monthList.map(({ year, month }) => {
      const date = new Date(year, month, 1);
      return {
        month: `${year}-${String(month + 1).padStart(2, '0')}`,
        monthLabel: date.toLocaleDateString('en-AU', { month: 'short' }),
        monthFullLabel: date.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' }),
        cashIn: 0,
        cashOut: 0,
      };
    });

    const addToMonth = (date: Date | null, amount: number, type: 'in' | 'out') => {
      // Refunds arrive as NEGATIVE payment rows (Stripe subscription refunds) and
      // must NET against income, not be dropped. Only a zero/NaN row is noise.
      if (!date || !Number.isFinite(amount) || amount === 0) return;
      if (date < startDate || date > endDate) return;
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthData = months.find(m => m.month === monthKey);
      if (monthData) {
        if (type === 'in') monthData.cashIn += amount;
        else monthData.cashOut += amount;
      }
    };

    invoices.forEach((invoice) => {
      if (invoice.Status === 'VOIDED') return;
      if (invoice.Payments && invoice.Payments.length > 0) {
        invoice.Payments.forEach(payment => {
          addToMonth(parseXeroDate(payment.Date), payment.Amount, 'in');
        });
      } else if (invoice.AmountPaid > 0) {
        addToMonth(parseXeroDate((invoice as any).Date || (invoice as any).DateString), invoice.AmountPaid, 'in');
      }
    });

    bills.forEach((bill) => {
      if (bill.Status === 'VOIDED') return;
      if (bill.Payments && bill.Payments.length > 0) {
        bill.Payments.forEach(payment => {
          addToMonth(parseXeroDate(payment.Date), payment.Amount, 'out');
        });
      } else if (bill.AmountPaid > 0) {
        addToMonth(parseXeroDate((bill as any).Date || (bill as any).DateString), bill.AmountPaid, 'out');
      }
    });

    const totalCashIn = months.reduce((sum, m) => sum + m.cashIn, 0);
    const totalCashOut = months.reduce((sum, m) => sum + m.cashOut, 0);

    return {
      monthlyData: months,
      totals: { cashIn: totalCashIn, cashOut: totalCashOut, difference: totalCashIn - totalCashOut },
    };
  }, [invoices, bills, startDate, endDate]);

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);

  const formatCompact = (amount: number) => {
    if (Math.abs(amount) >= 1000) {
      return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', notation: 'compact', maximumFractionDigits: 1 }).format(amount);
    }
    return formatCurrency(amount);
  };

  const handleChartClick = (dataPointIndex: number) => {
    if (dataPointIndex >= 0 && dataPointIndex < monthlyData.length) {
      setSelectedMonth(monthlyData[dataPointIndex]);
    }
  };

  // Chart options for the overview bar chart
  const chartOptions: ApexOptions = {
    chart: {
      type: 'bar',
      toolbar: { show: false },
      fontFamily: 'inherit',
      sparkline: { enabled: false },
      events: {
        dataPointSelection: (_event: any, _chartContext: any, config: { dataPointIndex: number }) => {
          handleChartClick(config.dataPointIndex);
        },
      },
      selection: { enabled: false },
    },
    plotOptions: {
      bar: { horizontal: false, columnWidth: '60%', borderRadius: 4 },
    },
    states: {
      hover: { filter: { type: 'darken', value: 0.9 } },
      active: { filter: { type: 'none' } },
    },
    dataLabels: { enabled: false },
    stroke: { show: false },
    xaxis: {
      categories: monthlyData.map(m => m.monthLabel),
      labels: { style: { colors: 'var(--bs-gray-600)', fontSize: '11px', cssClass: 'cursor-pointer' } },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        formatter: (val) => formatCompact(val),
        style: { colors: 'var(--bs-gray-600)', fontSize: '11px' },
      },
    },
    fill: { opacity: 1 },
    colors: ['#3E97FF', '#F1416C'],
    legend: {
      position: 'bottom',
      horizontalAlign: 'center',
      fontSize: '12px',
      markers: { width: 10, height: 10, radius: 10 },
      itemMargin: { horizontal: 15 },
      labels: { colors: 'var(--bs-gray-700)' },
    },
    tooltip: {
      y: { formatter: (val) => formatCurrency(val) },
      theme: 'dark',
      custom: ({ dataPointIndex }) => {
        const month = monthlyData[dataPointIndex];
        return `<div class="px-3 py-2">
          <div class="fw-bold mb-1">${month.monthFullLabel}</div>
          <div class="text-primary">Cash in: ${formatCurrency(month.cashIn)}</div>
          <div class="text-danger">Expenses: ${formatCurrency(month.cashOut)}</div>
          <div class="text-muted fs-8 mt-1">Click for details</div>
        </div>`;
      },
    },
    grid: {
      borderColor: 'var(--bs-gray-200)',
      strokeDashArray: 4,
      yaxis: { lines: { show: true } },
      xaxis: { lines: { show: false } },
    },
  };

  const chartSeries = [
    { name: 'Cash in', data: monthlyData.map(m => m.cashIn) },
    { name: 'Expenses', data: monthlyData.map(m => m.cashOut) },
  ];

  if (!xeroConnected) return null;

  const differenceIsPositive = totals.difference >= 0;

  const activeTab = VIEW_TABS.find(t => t.key === viewMode)!;

  const subtitleMap: Record<CashFlowViewMode, string> = {
    'overview': periodLabel,
    'net-trend': `Net cash flow trend · ${periodLabel}`,
    'receivables': 'Outstanding invoices by age',
    'top-customers': `Revenue by customer · ${periodLabel}`,
    'expenses': `Spending by supplier · ${periodLabel}`,
  };

  return (
    <div className="card mb-4 mb-md-5">
      <div className="card-header border-0 pt-4 pt-md-5 pb-3">
        <div className="d-flex justify-content-between align-items-start w-100 flex-wrap gap-3">
          <h3 className="card-title align-items-start flex-column mb-0">
            <span className="card-label fw-bold fs-5 fs-md-4 mb-1">
              {viewMode === 'overview' ? 'Cash in and expenses' : activeTab.label}
            </span>
            <span className="text-muted fs-7 d-none d-sm-inline">{subtitleMap[viewMode]}</span>
            <span className="text-muted fs-8">Recorded invoice and bill payments · <a href="/cashflow?view=reconcile">Reconcile ANZ statements</a></span>
          </h3>
          <div className="cashflow-actions d-flex align-items-center gap-2 flex-wrap">
            <button
              type="button"
              className="btn btn-sm btn-icon btn-light"
              onClick={() => setCollapsed((c) => !c)}
              title={collapsed ? 'Expand chart' : 'Collapse chart'}
            >
              <i className={`bi bi-chevron-${collapsed ? 'down' : 'up'}`} />
            </button>
            {/* Period selector */}
            <div className="dropdown">
              <button
                className="btn btn-sm btn-light dropdown-toggle d-flex align-items-center gap-1"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                style={{ fontSize: '0.8rem' }}
              >
                <i className="bi bi-calendar3" />
                <span>{period === 'custom' && customFrom && customTo ? periodLabel : PERIOD_OPTIONS.find(p => p.key === period)?.label ?? 'Period'}</span>
              </button>
              <ul className="dropdown-menu dropdown-menu-end py-2" style={{ minWidth: '180px' }}>
                {PERIOD_OPTIONS.filter(p => p.key !== 'custom').map((opt) => (
                  <li key={opt.key}>
                    <button
                      type="button"
                      className={`dropdown-item py-2 ${period === opt.key ? 'active' : ''}`}
                      onClick={() => setPeriod(opt.key)}
                    >
                      {opt.label}
                    </button>
                  </li>
                ))}
                <li><hr className="dropdown-divider" /></li>
                <li>
                  <button
                    type="button"
                    className={`dropdown-item py-2 d-flex align-items-center justify-content-between ${period === 'custom' ? 'active' : ''}`}
                    onClick={() => setPeriod('custom')}
                  >
                    Custom
                    {period === 'custom' && customFrom && customTo && <i className="bi bi-check text-success" />}
                  </button>
                </li>
                {period === 'custom' && (
                  <li className="px-3 py-2">
                    <div className="d-flex flex-column gap-2">
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={customFrom}
                        onChange={(e) => setCustomFrom(e.target.value)}
                        placeholder="From"
                      />
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={customTo}
                        onChange={(e) => setCustomTo(e.target.value)}
                        placeholder="To"
                      />
                    </div>
                  </li>
                )}
              </ul>
            </div>
            <div className="d-flex align-items-center gap-2 overflow-x-auto flex-nowrap cashflow-view-tabs">
              {VIEW_TABS.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  className={`btn btn-sm d-flex align-items-center gap-1 flex-shrink-0 ${
                    viewMode === tab.key
                      ? 'btn-primary'
                      : 'btn-light-primary'
                  }`}
                  onClick={() => setViewMode(tab.key)}
                  title={tab.label}
                  style={{ fontSize: '0.8rem', padding: '0.35rem 0.65rem' }}
                >
                  <i className={`bi ${tab.icon}`} style={{ fontSize: '0.85rem' }} />
                  <span className="d-none d-xl-inline">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {!collapsed && (
      <div className="card-body pt-2">
        {/* Overview (original) */}
        {viewMode === 'overview' && (
          <div className="row g-4">
            <div className="col-12 col-lg-4">
              <div className="d-flex flex-column gap-4">
                <div>
                  <div className="fs-2 fw-bold text-gray-800">{formatCurrency(totals.cashIn)}</div>
                  <div className="d-flex align-items-center gap-2">
                    <span className="bullet bullet-dot bg-primary" style={{ width: 10, height: 10 }} />
                    <span className="text-primary fs-7 fw-semibold">Cash in</span>
                  </div>
                </div>
                <div>
                  <div className="fs-2 fw-bold text-gray-800">-{formatCurrency(totals.cashOut)}</div>
                  <div className="d-flex align-items-center gap-2">
                    <span className="bullet bullet-dot" style={{ width: 10, height: 10, backgroundColor: '#F1416C' }} />
                    <span className="text-danger fs-7 fw-semibold">Expenses</span>
                  </div>
                </div>
                <div className="pt-2 border-top">
                  <div className={`fs-2 fw-bold ${differenceIsPositive ? 'text-success' : 'text-danger'}`}>
                    {differenceIsPositive ? '' : '-'}{formatCurrency(Math.abs(totals.difference))}
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <i className={`bi ${differenceIsPositive ? 'bi-arrow-up-circle-fill text-success' : 'bi-arrow-down-circle-fill text-danger'} fs-7`} />
                    <span className={`fs-7 fw-semibold ${differenceIsPositive ? 'text-success' : 'text-danger'}`}>
                      Difference
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-12 col-lg-8" style={{ overflow: 'hidden' }}>
              <div className="position-relative">
                <Chart options={chartOptions} series={chartSeries} type="bar" height={200} />
                <div className="text-center text-muted fs-8 mt-n2">
                  <i className="bi bi-hand-index me-1" />
                  Click on a month to see details
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Net Cash Flow Trend */}
        {viewMode === 'net-trend' && (
          <NetCashFlowView invoices={invoices} bills={bills} startDate={startDate} endDate={endDate} />
        )}

        {/* Receivables Aging */}
        {viewMode === 'receivables' && (
          <ReceivablesAgingView invoices={invoices} periodLabel={periodLabel} />
        )}

        {/* Top Customers */}
        {viewMode === 'top-customers' && (
          <TopCustomersView invoices={invoices} startDate={startDate} endDate={endDate} />
        )}

        {/* Expense Breakdown */}
        {viewMode === 'expenses' && (
          <ExpenseBreakdownView bills={bills} startDate={startDate} endDate={endDate} />
        )}
      </div>
      )}

      {/* Month Detail Modal (only relevant for overview) */}
      <MonthDetailModal
        show={!!selectedMonth}
        onHide={() => setSelectedMonth(null)}
        month={selectedMonth?.month || ''}
        monthLabel={selectedMonth?.monthFullLabel || ''}
        invoices={invoices}
        bills={bills}
      />
    </div>
  );
}

export default CashFlowSummary;
